import React, { useEffect, useState } from 'react';
import {
  fetchTokenConsumption,
  TokenConsumptionData,
  TokenConsumptionApiWise,
  TokenConsumptionDateWise,
  TokenConsumptionDayWise,
  fetchModelPricing,
  saveModelPricing,
  deleteModelPricing,
  ModelPricing
} from '../utils/api';
import './TokenConsumption.css';

interface TokenConsumptionProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const TokenConsumption: React.FC<TokenConsumptionProps> = ({ userRole }) => {
  const [data, setData] = useState<TokenConsumptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'api' | 'daily' | 'weekday' | 'pricing'>('api');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');

  // Model Pricing Settings States
  const [pricingList, setPricingList] = useState<ModelPricing[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);

  const [formModelName, setFormModelName] = useState('');
  const [formInputPrice, setFormInputPrice] = useState('');
  const [formOutputPrice, setFormOutputPrice] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const loadPricing = async () => {
    setPricingLoading(true);
    setPricingError(null);
    try {
      const res = await fetchModelPricing();
      setPricingList(res);
    } catch (err: any) {
      console.error('Error fetching model pricing:', err);
      setPricingError(err.message || 'Failed to fetch model pricing configurations');
    } finally {
      setPricingLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pricing') {
      loadPricing();
    }
  }, [activeTab]);

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSuccess('');
    setFormError('');

    if (!formModelName.trim() || formInputPrice === '' || formOutputPrice === '') {
      setFormError('All fields are required');
      return;
    }

    const inputNum = parseFloat(formInputPrice);
    const outputNum = parseFloat(formOutputPrice);

    if (isNaN(inputNum) || isNaN(outputNum)) {
      setFormError('Prices must be valid numbers');
      return;
    }

    try {
      await saveModelPricing({
        modelName: formModelName.trim(),
        inputPricePerMillion: inputNum,
        outputPricePerMillion: outputNum,
      });
      setFormSuccess('✓ Pricing configuration saved successfully');
      setFormModelName('');
      setFormInputPrice('');
      setFormOutputPrice('');
      setIsEditing(false);
      loadPricing();
      loadData(startDate, endDate, selectedCampaign);
    } catch (err: any) {
      setFormError(err.message || 'Failed to save pricing configuration');
    }
  };

  const handleDeletePricing = async (modelName: string) => {
    if (modelName === 'default') return;
    if (!window.confirm(`Are you sure you want to delete the pricing configuration for "${modelName}"?`)) return;

    try {
      await deleteModelPricing(modelName);
      loadPricing();
      loadData(startDate, endDate, selectedCampaign);
    } catch (err: any) {
      alert(err.message || 'Failed to delete pricing configuration');
    }
  };

  const handleEditClick = (item: ModelPricing) => {
    setFormModelName(item.modelName);
    setFormInputPrice(item.inputPricePerMillion.toString());
    setFormOutputPrice(item.outputPricePerMillion.toString());
    setIsEditing(true);
    setFormError('');
    setFormSuccess('');
  };

  const loadData = async (start?: string, end?: string, campaign?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTokenConsumption(start, end, campaign);
      setData(res);
    } catch (err: any) {
      console.error('Error fetching token consumption:', err);
      setError(err.message || 'Failed to fetch token consumption metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole !== 'customer') {
      loadData();
    }
  }, [userRole]);

  if (userRole === 'customer') {
    return (
      <div className="token-view-error">
        <h4>Access Denied</h4>
        <p>You do not have permission to view system billing and token consumption logs.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="token-loading-container">
        <div className="token-spinner"></div>
        <p>Loading token consumption analytics...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="token-error-container">
        <h4>⚠️ Error loading analytics</h4>
        <p>{error || 'No analytics data available.'}</p>
        <button onClick={() => loadData(startDate, endDate)} className="token-retry-btn">Retry Loading</button>
      </div>
    );
  }

  // Calculate totals
  const exchangeRate = data?.usdToInrRate || 95.75;
  const totalPromptTokens = data.apiWise.reduce((sum, item) => sum + item.totalPromptTokens, 0);
  const totalCompletionTokens = data.apiWise.reduce((sum, item) => sum + item.totalCompletionTokens, 0);
  const totalOverallTokens = totalPromptTokens + totalCompletionTokens;
  const totalCost = data.apiWise.reduce((sum, item) => sum + item.totalCost, 0);
  const totalRequests = data.apiWise.reduce((sum, item) => sum + item.requestCount, 0);

  // Helper for percentage
  const getPercentage = (value: number) => {
    if (totalOverallTokens === 0) return 0;
    return Math.round((value / totalOverallTokens) * 100);
  };

  const formatCost = (amount: number) => {
    if (amount === 0) return '$0.00 (₹0.00)';
    
    const usdToInr = exchangeRate;
    const amountInr = amount * usdToInr;

    let usdStr = '';
    if (amount < 0.01) {
      usdStr = '$' + amount.toFixed(5);
    } else {
      usdStr = amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    let inrStr = '';
    if (amountInr < 0.01) {
      inrStr = '₹' + amountInr.toFixed(5);
    } else {
      inrStr = amountInr.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return `${usdStr} (${inrStr})`;
  };

  // Find max daily tokens for daily SVG scale
  const maxDailyTokens = data.dateWise.length > 0
    ? Math.max(...data.dateWise.map(d => d.totalTokens))
    : 1;

  // Find max weekday tokens for weekday SVG scale
  const maxWeekdayTokens = data.dayWise.length > 0
    ? Math.max(...data.dayWise.map(w => w.totalTokens))
    : 1;

  // Full day of week mapping to guarantee order and index naming if MySQL names vary
  const daysOfWeekFull = [
    { key: 2, label: 'Mon' },
    { key: 3, label: 'Tue' },
    { key: 4, label: 'Wed' },
    { key: 5, label: 'Thu' },
    { key: 6, label: 'Fri' },
    { key: 7, label: 'Sat' },
    { key: 1, label: 'Sun' }
  ];

  // Map database day results by weekday number
  const weekdayMap: Record<number, TokenConsumptionDayWise> = {};
  data.dayWise.forEach(d => {
    weekdayMap[d.dayOfWeek] = d;
  });

  return (
    <div className="token-view-container">
      {/* Date Range & Campaign Filter */}
      <div className="token-filter-bar">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="filter-date-input"
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="filter-date-input"
          />
        </div>
        <div className="filter-group">
          <label>Campaign:</label>
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="filter-date-input"
            style={{ minWidth: '150px' }}
          >
            <option value="">All Campaigns</option>
            {data.campaigns.map((campName) => (
              <option key={campName} value={campName}>
                {campName}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => loadData(startDate, endDate, selectedCampaign)}
          className="filter-apply-btn"
        >
          🔍 Apply Filter
        </button>
        {(startDate || endDate || selectedCampaign) && (
          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setSelectedCampaign('');
              loadData('', '', '');
            }}
            className="filter-clear-btn"
          >
            Clear
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="token-summary-grid">
        <div className="token-summary-card primary">
          <div className="card-header">
            <span className="card-icon">🪙</span>
            <span className="card-title">Total Tokens</span>
          </div>
          <div className="card-value">{totalOverallTokens.toLocaleString()}</div>
          <p className="card-subtitle">Accumulated API tokens billed</p>
        </div>

        <div className="token-summary-card success">
          <div className="card-header">
            <span className="card-icon">💵</span>
            <span className="card-title">Est. Billed Cost</span>
          </div>
          <div className="card-value success-color">{formatCost(totalCost)}</div>
          <p className="card-subtitle">Estimated cost in USD</p>
        </div>

        <div className="token-summary-card">
          <div className="card-header">
            <span className="card-icon">📥</span>
            <span className="card-title">Prompt / Input</span>
          </div>
          <div className="card-value">{totalPromptTokens.toLocaleString()}</div>
          <p className="card-subtitle">
            {getPercentage(totalPromptTokens)}% of total usage
          </p>
        </div>

        <div className="token-summary-card">
          <div className="card-header">
            <span className="card-icon">📤</span>
            <span className="card-title">Completion / Output</span>
          </div>
          <div className="card-value">{totalCompletionTokens.toLocaleString()}</div>
          <p className="card-subtitle">
            {getPercentage(totalCompletionTokens)}% of total usage
          </p>
        </div>

        <div className="token-summary-card">
          <div className="card-header">
            <span className="card-icon">🔄</span>
            <span className="card-title">Requests / Turns</span>
          </div>
          <div className="card-value">{totalRequests.toLocaleString()}</div>
          <p className="card-subtitle">Total API interactions logged</p>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="token-tabs-bar">
        <button
          className={`tab-btn ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          🔌 API & Model Share
        </button>
        <button
          className={`tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          📈 Daily Trend (30d)
        </button>
        <button
          className={`tab-btn ${activeTab === 'weekday' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekday')}
        >
          🗓️ Day of Week
        </button>
        <button
          className={`tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          ⚙️ Price Settings
        </button>
        <button onClick={() => loadData(startDate, endDate, selectedCampaign)} className="tab-refresh-btn" title="Refresh Data">
          🔄 Refresh
        </button>
      </div>

      {/* Tabs Content */}
      <div className="token-card-body">
        
        {/* TAB 1: API / MODEL SHARE */}
        {activeTab === 'api' && (
          <div className="tab-content-api">
            <div className="section-title-desc">
              <h4>Model Volume Share</h4>
              <p>Breakdown of Google AI tokens consumed by individual endpoint and AI model.</p>
            </div>
            
            {data.apiWise.length === 0 ? (
              <div className="empty-state">No token usage logged yet. Make a call to see analytics.</div>
            ) : (
              <div className="table-responsive">
                <table className="token-data-table">
                  <thead>
                    <tr>
                      <th>API Integration</th>
                      <th>Model Name</th>
                      <th className="text-right">Prompt Tokens</th>
                      <th className="text-right">Completion Tokens</th>
                      <th className="text-right">Total Tokens</th>
                      <th className="text-right">Est. Cost</th>
                      <th className="text-right">Interactions</th>
                      <th style={{ width: '150px' }}>Usage share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.apiWise.map((row, idx) => {
                      const share = getPercentage(row.totalTokens);
                      return (
                        <tr key={idx}>
                          <td className="font-semibold">{row.apiName}</td>
                          <td><code className="model-code">{row.modelName}</code></td>
                          <td className="text-right font-mono">{row.totalPromptTokens.toLocaleString()}</td>
                          <td className="text-right font-mono">{row.totalCompletionTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold">{row.totalTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold success-color">{formatCost(row.totalCost)}</td>
                          <td className="text-right font-mono">{row.requestCount.toLocaleString()}</td>
                          <td>
                            <div className="share-progress-container">
                              <div className="share-progress-bar" style={{ width: `${share}%` }}></div>
                              <span className="share-percent">{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: DAILY USAGE TREND */}
        {activeTab === 'daily' && (
          <div className="tab-content-daily">
            <div className="section-title-desc">
              <h4>Daily Consumption Trend</h4>
              <p>Visual view of overall tokens consumed day-by-day over the last 30 days.</p>
            </div>

            {data.dateWise.length === 0 ? (
              <div className="empty-state">No daily usage recorded yet.</div>
            ) : (
              <div className="daily-analytics-layout">
                {/* SVG Column Chart */}
                <div className="daily-chart-card">
                  <div className="chart-wrapper">
                    <svg viewBox="0 0 600 240" className="trend-svg-chart">
                      {/* Grid Lines */}
                      <line x1="40" y1="30" x2="580" y2="30" stroke="#f1f5f9" strokeDasharray="3 3" />
                      <line x1="40" y1="90" x2="580" y2="90" stroke="#f1f5f9" strokeDasharray="3 3" />
                      <line x1="40" y1="150" x2="580" y2="150" stroke="#f1f5f9" strokeDasharray="3 3" />
                      <line x1="40" y1="210" x2="580" y2="210" stroke="#cbd5e1" strokeWidth="1" />
                      
                      {/* Y-Axis Label */}
                      <text x="35" y="34" className="chart-y-label">Max</text>
                      <text x="35" y="124" className="chart-y-label">50%</text>
                      <text x="35" y="214" className="chart-y-label">0</text>
                      
                      {/* Bars */}
                      {(() => {
                        const chartData = [...data.dateWise].reverse(); // oldest first for trend
                        const barWidth = Math.max(8, Math.floor(480 / chartData.length) - 6);
                        const startX = 60;
                        const spacing = Math.floor(480 / chartData.length);
                        
                        return chartData.map((d, i) => {
                          const barHeight = Math.max(4, Math.round((d.totalTokens / maxDailyTokens) * 160));
                          const x = startX + i * spacing;
                          const y = 210 - barHeight;
                          
                          // Format date nicely (MM-DD)
                          const parts = d.date.split('-');
                          const label = parts.length > 2 ? `${parts[1]}/${parts[2]}` : d.date;

                          return (
                            <g key={i} className="chart-bar-group">
                              <rect
                                x={x}
                                y={y}
                                width={barWidth}
                                height={barHeight}
                                rx="2"
                                className="chart-bar-rect"
                              />
                              {/* Tooltip Hover value */}
                              <title>{`${d.date}\nTokens: ${d.totalTokens.toLocaleString()}\nCalls: ${d.requestCount}`}</title>
                              
                              {/* X Axis Labels (Draw every 3rd or 4th label to avoid overlapping) */}
                              {(i % Math.ceil(chartData.length / 8) === 0 || i === chartData.length - 1) && (
                                <text x={x + barWidth / 2} y="225" className="chart-x-label">
                                  {label}
                                </text>
                              )}
                            </g>
                          );
                        });
                      })()}
                    </svg>
                  </div>
                </div>

                {/* Table Data */}
                <div className="table-responsive">
                  <table className="token-data-table compact">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">Prompt Tokens</th>
                        <th className="text-right">Completion Tokens</th>
                        <th className="text-right">Total Tokens</th>
                        <th className="text-right">Est. Cost</th>
                        <th className="text-right">Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dateWise.map((row, idx) => (
                        <tr key={idx}>
                          <td className="font-semibold">{row.date}</td>
                          <td className="text-right font-mono">{row.totalPromptTokens.toLocaleString()}</td>
                          <td className="text-right font-mono">{row.totalCompletionTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold">{row.totalTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold success-color">{formatCost(row.totalCost)}</td>
                          <td className="text-right font-mono">{row.requestCount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: WEEKDAY INSIGHTS */}
        {activeTab === 'weekday' && (
          <div className="tab-content-weekday">
            <div className="section-title-desc">
              <h4>Day of the Week Insights</h4>
              <p>Analyze usage patterns to see which days of the week generate the most API token consumption.</p>
            </div>

            <div className="weekday-insights-layout">
              {/* SVG Horizontal Bar Chart */}
              <div className="weekday-chart-card">
                <div className="weekday-bars-container">
                  {daysOfWeekFull.map((dayDef) => {
                    const row = weekdayMap[dayDef.key];
                    const tokens = row ? row.totalTokens : 0;
                    const prompt = row ? row.totalPromptTokens : 0;
                    const completion = row ? row.totalCompletionTokens : 0;
                    const requests = row ? row.requestCount : 0;
                    const percentWidth = maxWeekdayTokens > 1 ? (tokens / maxWeekdayTokens) * 100 : 0;

                    return (
                      <div key={dayDef.key} className="weekday-row-bar">
                        <div className="weekday-label-col">{dayDef.label}</div>
                        <div className="weekday-progress-col">
                          <div className="progress-track">
                            <div 
                              className="progress-fill-val" 
                              style={{ width: `${Math.max(2, percentWidth)}%` }}
                              title={`Total Tokens: ${tokens.toLocaleString()}`}
                            ></div>
                          </div>
                        </div>
                        <div className="weekday-value-col font-mono">
                          {tokens > 0 ? (
                            <>
                              <strong>{tokens.toLocaleString()}</strong>
                              <span className="weekday-detail-sub">({formatCost(row ? row.totalCost : 0)})</span>
                            </>
                          ) : (
                            <span className="inactive-text">0 tokens</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Table Data */}
              <div className="table-responsive">
                <table className="token-data-table">
                  <thead>
                    <tr>
                      <th>Day Name</th>
                      <th className="text-right">Prompt Tokens</th>
                      <th className="text-right">Completion Tokens</th>
                      <th className="text-right">Total Tokens</th>
                      <th className="text-right">Est. Cost</th>
                      <th className="text-right">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daysOfWeekFull.map((dayDef) => {
                      const row = weekdayMap[dayDef.key];
                      if (!row) {
                        return (
                          <tr key={dayDef.key} className="inactive-row">
                            <td className="font-semibold">{dayDef.label}day</td>
                            <td className="text-right font-mono">-</td>
                            <td className="text-right font-mono">-</td>
                            <td className="text-right font-mono font-semibold">-</td>
                            <td className="text-right font-mono font-semibold">-</td>
                            <td className="text-right font-mono">0</td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={dayDef.key}>
                          <td className="font-semibold">{row.dayName}</td>
                          <td className="text-right font-mono">{row.totalPromptTokens.toLocaleString()}</td>
                          <td className="text-right font-mono">{row.totalCompletionTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold">{row.totalTokens.toLocaleString()}</td>
                          <td className="text-right font-mono font-semibold success-color">{formatCost(row.totalCost)}</td>
                          <td className="text-right font-mono">{row.requestCount.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: PRICING SETTINGS */}
        {activeTab === 'pricing' && (
          <div className="tab-content-pricing">
            <div className="section-title-desc">
              <h4>Model Billing Configuration</h4>
              <p>Configure dynamic input/output pricing (in USD per 1,000,000 tokens) for individual Gemini models.</p>
            </div>

            <div className="pricing-grid-layout">
              {/* Form Card */}
              <div className="pricing-form-card">
                <h5>{isEditing ? '✏️ Edit Model Pricing' : '➕ Add Model Pricing'}</h5>
                <form onSubmit={handleSavePricing} className="pricing-form">
                  <div className="form-group-item">
                    <label>Model Identifier</label>
                    <input
                      type="text"
                      placeholder="e.g. gemini-2.5-pro"
                      value={formModelName}
                      onChange={(e) => setFormModelName(e.target.value)}
                      disabled={isEditing && formModelName === 'default'}
                      className="pricing-form-input"
                    />
                    {isEditing && formModelName === 'default' && (
                      <span className="input-helper-text">The default fallback rates cannot be renamed.</span>
                    )}
                  </div>
                  
                  <div className="form-row-pricing">
                    <div className="form-group-item">
                      <label>Input Rate ($ / 1M tokens)</label>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        placeholder="0.0750"
                        value={formInputPrice}
                        onChange={(e) => setFormInputPrice(e.target.value)}
                        className="pricing-form-input"
                      />
                    </div>
                    <div className="form-group-item">
                      <label>Output Rate ($ / 1M tokens)</label>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        placeholder="0.3000"
                        value={formOutputPrice}
                        onChange={(e) => setFormOutputPrice(e.target.value)}
                        className="pricing-form-input"
                      />
                    </div>
                  </div>

                  {formError && <div className="pricing-msg-error">{formError}</div>}
                  {formSuccess && <div className="pricing-msg-success">{formSuccess}</div>}

                  <div className="pricing-form-actions">
                    <button type="submit" className="pricing-submit-btn">
                      {isEditing ? 'Update Configuration' : 'Save Configuration'}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setFormModelName('');
                          setFormInputPrice('');
                          setFormOutputPrice('');
                          setFormError('');
                          setFormSuccess('');
                        }}
                        className="pricing-cancel-btn"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* List Card */}
              <div className="pricing-list-card">
                <h5>Active Configurations</h5>
                {pricingLoading ? (
                  <div className="pricing-sub-loading">Loading pricing details...</div>
                ) : pricingError ? (
                  <div className="pricing-sub-error">{pricingError}</div>
                ) : pricingList.length === 0 ? (
                  <div className="empty-state">No configurations found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="token-data-table compact">
                      <thead>
                        <tr>
                          <th>Model Name</th>
                          <th className="text-right">Input Rate (1M)</th>
                          <th className="text-right">Output Rate (1M)</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingList.map((item, idx) => (
                          <tr key={idx} className={item.modelName === 'default' ? 'default-pricing-row' : ''}>
                            <td className="font-semibold">
                              <code>{item.modelName}</code>
                              {item.modelName === 'default' && <span className="default-pill-badge">Fallback Default</span>}
                            </td>
                            <td className="text-right font-mono font-semibold">${item.inputPricePerMillion.toFixed(4)} (₹{(item.inputPricePerMillion * exchangeRate).toFixed(2)})</td>
                            <td className="text-right font-mono font-semibold">${item.outputPricePerMillion.toFixed(4)} (₹{(item.outputPricePerMillion * exchangeRate).toFixed(2)})</td>
                            <td className="text-center">
                              <button
                                onClick={() => handleEditClick(item)}
                                className="pricing-edit-action-btn"
                                title="Edit Rates"
                              >
                                ✏️ Edit
                              </button>
                              {item.modelName !== 'default' && (
                                <button
                                  onClick={() => handleDeletePricing(item.modelName)}
                                  className="pricing-delete-action-btn"
                                  title="Delete Model Configuration"
                                >
                                  🗑️ Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TokenConsumption;
